import cn from 'clsx'
import React, { useMemo, useState, useCallback, useContext } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Heading from 'components/Luxkit/Typography/Heading'
import BodyText from 'components/Luxkit/Typography/BodyText'
import LineAngleLeftBIcon from 'components/Luxkit/Icons/line/LineAngleLeftBIcon'
import LineAngleRightBIcon from 'components/Luxkit/Icons/line/LineAngleRightBIcon'
import CircleCheckboxInput from 'components/Luxkit/Checkbox/CircleCheckboxInput'
import moment from 'moment'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { MONTH_NAMES } from 'constants/dates'
import { without } from 'lib/array/arrayUtils'
import Clickable from 'components/Common/Clickable/Clickable'
import Group from 'components/utils/Group'
import IconButton from 'components/Luxkit/Button/IconButton'
import { KEYBOARD_MODE_CSS_VAR } from 'contexts/InputModeContext'

const MonthGridWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(8)};
`

const YearsControl = styled.div`
  margin-top: ${rem(16)};
  display: flex;
  align-items: center;
  gap: ${rem(16)};
`

const MonthGrid = styled.div`
  margin-top: ${rem(12)};
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(6, 1fr);

    &.grouped-years {
      grid-template-columns: repeat(7, 1fr);
    }
  }

  &.drawer {
    grid-template-columns: 1fr 1fr 1fr;
  }
`

const MonthGridItem = styled(Clickable)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${rem(12)};
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.palette.neutral.five};
  border-radius: ${props => props.theme.borderRadius.M};
  transition: border-color 0.2s, box-shadow 0.2s, color 0.2s;
  gap: ${rem(4)};

  &:not(:disabled) {
    &:hover {
      border-color: ${props => props.theme.palette.neutral.one};
    }

    &:focus {
      outline: var(${KEYBOARD_MODE_CSS_VAR}, 2px solid ${props => props.theme.palette.neutral.five});
      outline-offset: var(${KEYBOARD_MODE_CSS_VAR}, 2px);
    }

    &.selected {
      box-shadow: inset 0px 0px 0px 2px ${({ theme }) => theme.palette.neutral.one};
    }
  }

  &:disabled {
    color: ${props => props.theme.palette.neutral.four};
  }

  &.selected {
    box-shadow: inset 0px 0px 0px 2px ${({ theme }) => theme.palette.neutral.one};
  }
`

interface Props {
  onSelect?: () => void;
  onChange?: (value: App.CruiseGlobalFilters) => void;
  drawerMode?: boolean;
}

const currentYear = new Date().getFullYear()
const startOfMonth = moment().startOf('month')
function CruiseWhenMonth({
  onSelect,
  onChange,
  drawerMode,
}: Props) {
  const {
    flexibleMonths,
  } = useContext(GlobalSearchStateContext)
  const searchDispatch = useContext(GlobalSearchDispatchContext)
  const [selectedYear, setYear] = useState(currentYear)
  const departureMonths = useMemo(() => flexibleMonths?.split(',') ?? [], [flexibleMonths])

  // actions
  const needToGroupYears = useCallback((year: number) => {
    const monthsLeft = 12 - startOfMonth.month()
    return (String(year) === startOfMonth.format('YYYY') && monthsLeft < 4)
  }, [])

  const nextYear = useCallback(() => {
    if (needToGroupYears(selectedYear)) {
      setYear(selectedYear + 2)
    } else {
      setYear(selectedYear + 1)
    }
  }, [selectedYear, needToGroupYears])

  const prevYear = useCallback(() => {
    if (needToGroupYears(selectedYear - 2)) {
      setYear(selectedYear - 2)
    } else {
      setYear(selectedYear - 1)
    }
  }, [selectedYear, needToGroupYears])

  const onMonthSelect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const month = e.currentTarget.dataset.month
    let nextMonths = []
    const alreadyExists = departureMonths.includes(month)

    if (alreadyExists) {
      nextMonths = without(departureMonths, month)
    } else {
      nextMonths = [...departureMonths, month]
    }
    if (onChange) onChange({ flexibleMonths: nextMonths.join(',') })
    searchDispatch({
      type: GlobalSearchStateActions.SET_FLEXIBLE_MONTH_RANGE,
      flexibleMonths: nextMonths.join(','),
    })

    if (onSelect) onSelect()
  }, [departureMonths, searchDispatch, onSelect, onChange])

  const visibleYears = useMemo(() => {
    const visibleYears = [selectedYear]

    if (needToGroupYears(selectedYear)) {
      visibleYears.push(selectedYear + 1)
    }

    return visibleYears
  }, [selectedYear, needToGroupYears])

  const monthList = useMemo(() => {
    return visibleYears.flatMap(year => {
      return MONTH_NAMES.map((monthName, index) => {
        const key = `${year}-${(index + 1).toString().padStart(2, '0')}`
        return {
          key,
          year,
          name: monthName,
          disable: moment(`${key}-01`).isBefore(startOfMonth, 'month'),
          isSelected: departureMonths.includes(key),
        }
      })
    }).filter(month => !month.disable)
  }, [departureMonths, visibleYears])

  const groupYear = needToGroupYears(selectedYear)

  return <MonthGridWrapper>
    <Heading variant="heading6">When would you like to sail?</Heading>

    <YearsControl>
      <IconButton
        kind="tertiary"
        onClick={prevYear}
        disabled={selectedYear === currentYear}
      >
        <LineAngleLeftBIcon />
      </IconButton>

      <Heading variant="heading6">{visibleYears.join(' - ')}</Heading>

      <IconButton
        kind="tertiary"
        onClick={nextYear}
        disabled={selectedYear === (currentYear + 3)}
      >
        <LineAngleRightBIcon />
      </IconButton>
    </YearsControl>

    <MonthGrid
      className={cn({ 'grouped-years': groupYear, drawer: drawerMode })}
    >
      {monthList.map(({ name, disable, key, isSelected, year }) => <MonthGridItem
        key={key}
        data-month={key}
        className={cn({ selected: isSelected })}
        disabled={disable}
        onClick={onMonthSelect}
      >
        <Group direction="vertical" gap={8} horizontalAlign="center">
          <BodyText variant="medium" weight="bold">{name}</BodyText>
          {groupYear && <BodyText variant="medium" weight="bold">{year}</BodyText>}
          <CircleCheckboxInput checked={isSelected} tabIndex={-1} />
        </Group>
      </MonthGridItem>)}
    </MonthGrid>
  </MonthGridWrapper>
}
export default CruiseWhenMonth
